import { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Wallet, Pencil, Info } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '../lib/api';

/**
 * OpeningBalanceEditor
 *
 * A reusable card + dialog for viewing/editing an opening balance with a
 * direction toggle (Dr/Cr). Works for both vendors and staff.
 *
 * Props:
 *  - entityLabel: string — "Vendor" | "Staff"
 *  - value: { opening_balance_amount, opening_balance_direction, opening_balance_as_of }
 *  - directionOptions: [{ value: 'we_owe', label: 'We owe them (Cr)', hint: '...' }, ...]
 *  - onSave: async (patchBody) => void  — patchBody has snake_case fields.
 *  - testid: string — base data-testid.
 */
export function OpeningBalanceEditor({
    entityLabel = 'Vendor',
    value,
    directionOptions,
    onSave,
    testid = 'opening-balance',
}) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [direction, setDirection] = useState(directionOptions[0].value);
    const [asOf, setAsOf] = useState('');
    const [saving, setSaving] = useState(false);

    const currentAmount = Number(value?.opening_balance_amount || 0);
    const currentDir = value?.opening_balance_direction || null;
    const currentAsOf = value?.opening_balance_as_of || null;
    const currentDirLabel = (
        directionOptions.find((o) => o.value === currentDir)?.shortLabel
        || (currentDir ? currentDir : '—')
    );
    const hasValue = currentAmount > 0 && !!currentDir;

    useEffect(() => {
        if (open) {
            setAmount(String(currentAmount || ''));
            setDirection(currentDir || directionOptions[0].value);
            setAsOf(currentAsOf || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    async function save() {
        const parsed = Number(amount);
        if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error('Enter a non-negative amount');
            return;
        }
        setSaving(true);
        try {
            await onSave({
                opening_balance_amount: parsed,
                opening_balance_direction: parsed > 0 ? direction : null,
                opening_balance_as_of: asOf || null,
            });
            toast.success('Opening balance saved');
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save opening balance');
        } finally {
            setSaving(false);
        }
    }

    async function clearBalance() {
        setSaving(true);
        try {
            await onSave({
                opening_balance_amount: 0,
                opening_balance_direction: null,
                opening_balance_as_of: null,
            });
            toast.success('Opening balance cleared');
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to clear opening balance');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Card className="p-5 border-border/80" data-testid={`${testid}-card`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                        <Wallet size={18} />
                    </div>
                    <div>
                        <div className="font-heading text-lg">Opening balance</div>
                        <div className="text-xs text-muted-foreground">
                            Enter the outstanding amount that existed <em>before</em> you started tracking
                            this {entityLabel.toLowerCase()} in Ease My Bill.
                        </div>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setOpen(true)}
                    data-testid={`${testid}-edit-btn`}
                >
                    <Pencil size={14} /> {hasValue ? 'Edit' : 'Set'}
                </Button>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border border-border bg-card/70">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Amount</div>
                    <div
                        className="font-heading text-2xl mt-1 tabular-nums font-mono"
                        data-testid={`${testid}-amount`}
                    >
                        {fmtMoney(currentAmount)}
                    </div>
                </div>
                <div className="p-3 rounded-lg border border-border bg-card/70">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Direction</div>
                    <div className="mt-2" data-testid={`${testid}-direction`}>
                        {hasValue ? (
                            <Badge variant="secondary" className="text-xs">
                                {currentDirLabel}
                            </Badge>
                        ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                        )}
                    </div>
                </div>
                <div className="p-3 rounded-lg border border-border bg-card/70">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">As of</div>
                    <div className="mt-1 text-sm font-mono tabular-nums" data-testid={`${testid}-as-of`}>
                        {currentAsOf || '—'}
                    </div>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid={`${testid}-dialog`}>
                    <DialogHeader>
                        <DialogTitle>
                            {hasValue ? 'Edit opening balance' : 'Set opening balance'}
                        </DialogTitle>
                        <DialogDescription>
                            Records legacy outstanding not represented by uploaded bills or logged
                            payments. It will be reflected in the outstanding total.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Amount (₹) <span className="text-[hsl(var(--destructive))]">*</span></Label>
                            <Input
                                data-testid={`${testid}-amount-input`}
                                type="number"
                                min={0}
                                step={0.01}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 inline-block">Direction</Label>
                            <RadioGroup
                                value={direction}
                                onValueChange={setDirection}
                                className="space-y-2"
                                data-testid={`${testid}-direction-radio`}
                            >
                                {directionOptions.map((opt) => (
                                    <label
                                        key={opt.value}
                                        className="flex items-start gap-3 p-3 rounded-md border border-border hover:bg-muted/40 cursor-pointer"
                                    >
                                        <RadioGroupItem
                                            value={opt.value}
                                            id={`${testid}-${opt.value}`}
                                            className="mt-0.5"
                                            data-testid={`${testid}-radio-${opt.value}`}
                                        />
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium">{opt.label}</div>
                                            {opt.hint && (
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    {opt.hint}
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </RadioGroup>
                        </div>
                        <div>
                            <Label>As of (optional)</Label>
                            <Input
                                data-testid={`${testid}-as-of-input`}
                                type="date"
                                value={asOf}
                                onChange={(e) => setAsOf(e.target.value)}
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                                Effective date — usually the day before you started using Ease My Bill.
                            </div>
                        </div>
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertTitle className="font-heading text-sm">How this works</AlertTitle>
                            <AlertDescription className="text-xs">
                                Opening balance affects the <strong>all-time</strong> outstanding total, not
                                monthly summaries. It's applied on top of billed vs. paid maths.
                            </AlertDescription>
                        </Alert>
                    </div>
                    <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                        {hasValue && (
                            <Button
                                variant="ghost"
                                className="text-[hsl(var(--price-increase-fg))]"
                                onClick={clearBalance}
                                disabled={saving}
                                data-testid={`${testid}-clear-btn`}
                            >
                                Clear
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={saving}
                            className="sm:ml-auto"
                            data-testid={`${testid}-cancel-btn`}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            data-testid={`${testid}-save-btn`}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
